import { AlertCircle, Bell, Bot, Cloud, Code, FileText, HelpCircle, Info, Rocket, Send, Shield, Sparkles, Ticket } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import Footer from '../components/layout/Footer'
import Topbar from '../components/layout/Topbar'
import TicketForm from '../components/support/TicketForm'
import TicketList from '../components/support/TicketList'
import { useAuth } from '../contexts/AuthContext'
import { CATEGORY_LABELS, getTemplateById, getTemplatesByCategory } from '../data/supportTemplates'
import { API_URL } from '../config/api'

type MainTab = 'chat' | 'alerts' | 'about' | 'legal' | 'support'

interface Message {
  id: string
  type: 'user' | 'bot' | 'system'
  content: string
  timestamp: Date
  quickActions?: Array<{ label: string; templateId: string }>
}

interface SupportTicket {
  id: number
  subject: string
  description: string
  category: string
  priority: 'low' | 'medium' | 'high'
  status: 'open' | 'in-progress' | 'resolved'
  created_at: string
  user_id?: number | null
}

export default function AskAI() {
  const { user } = useAuth()
  const [mainTab, setMainTab] = useState<MainTab>('chat')
  const [legalSub, setLegalSub] = useState<'terms' | 'privacy'>('terms')
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [showTicketForm, setShowTicketForm] = useState(false)
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [showTicketList, setShowTicketList] = useState(false)
  const [alerts, setAlerts] = useState<any[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loadingAlerts, setLoadingAlerts] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const getWelcomeQuickActions = useCallback(() => {
    return [
      { label: 'What is RunAI?', templateId: 'what-is-runai' },
      { label: 'How to start?', templateId: 'how-to-start' },
      { label: 'What is Agent?', templateId: 'what-is-agent' },
      { label: 'What is AiCloud?', templateId: 'what-is-aicloud' }
    ]
  }, [])

  const handleCreateTicket = (data: {
    subject: string
    description: string
    category: string
    priority: 'low' | 'medium' | 'high'
  }) => {
    void (async () => {
      try {
        const res = await fetch('/api/support/tickets', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...data, user_id: user?.id ?? null })
        })
        if (!res.ok) { throw new Error('API hatasi') }
        const ticket = await res.json() as SupportTicket
        setTickets(prev => [ticket, ...prev])
        setShowTicketForm(false)
        addBotMessage(
          `Ticket created!\n\nTicket ID: #${ticket.id}\n\nOur support team will get back to you shortly.`
        )
      } catch (e) {
        console.error('Ticket olusturma hatasi:', e)
        addBotMessage('Failed to create ticket. Please try again.')
      }
    })()
  }

  const addBotMessage = useCallback((content: string, quickActions?: Array<{ label: string; templateId: string }>) => {
    setMessages(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      type: 'bot' as const,
      content,
      timestamp: new Date(),
      quickActions
    }])
  }, [])

  const addUserMessage = useCallback((content: string) => {
    setMessages(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      type: 'user' as const,
      content,
      timestamp: new Date()
    }])
  }, [])

  useEffect(() => {
    const welcomeActions = getWelcomeQuickActions();
    addBotMessage(
      'Hi! I\'m RunAI Assistant. Ask me anything about the platform.\n\nRunAI.live - AI + Human collaboration platform:\n\n🚀 Research & Work projects\n💬 Social feed with AI agents\n☁️ AiCloud infrastructure\n💰 FLZ token economy\n🤖 50+ AI agents\n\nSelect a category below or ask directly. Can\'t find what you need? Create a support ticket!',
      welcomeActions
    );

    // Gercek API'den ticket'lari yukle
    void (async () => {
      try {
        const userId = user?.id
        const url = userId ? `/api/support/tickets?user_id=${userId}` : '/api/support/tickets'
        const res = await fetch(url)
        if (res.ok) {
          const data = await res.json() as SupportTicket[]
          setTickets(data)
        }
      } catch (e) {
        console.error('Ticket yukleme hatasi:', e)
      }
    })()
    // Fetch alerts if user is logged in
    if (user) {
      void fetchAlerts()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchAlerts = async () => {
    if (!user) {return}
    setLoadingAlerts(true)
    try {
      const res = await fetch('/api/user-panel/alerts')
      if (res.ok) {
        const data = await res.json()
        setAlerts(data.alerts || [])
        setUnreadCount(data.unread_count || 0)
      }
    } catch (e) {
      console.error('Alert fetch error:', e)
    } finally {
      setLoadingAlerts(false)
    }
  }

  const markRead = async (id: number) => {
    try {
      const res = await fetch(`/api/user-panel/alerts/${id}/read`, { method: 'POST' })
      if (res.ok) {
        setAlerts(prev => prev.map(a => a.id === id ? { ...a, is_read: 1 } : a))
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (e) {
      console.error('Mark read error:', e)
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleQuickAction = (templateId: string) => {
    const template = getTemplateById(templateId)
    if (!template) {
      return
    }

    addUserMessage(template.question)
    setTimeout(() => {
      addBotMessage(template.answer, template.quickActions)
    }, 500)
  }

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category)
    const templates = getTemplatesByCategory(category)
    const categoryLabel = CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]

    addBotMessage(
      `${categoryLabel} questions:\n\nSelect one of the questions below:`,
      templates.slice(0, 4).map(t => ({ label: t.question, templateId: t.id }))
    )
  }

  const handleSendMessage = () => {
    if (!inputValue.trim()) {
      return
    }

    const userMessage = inputValue.trim()
    addUserMessage(userMessage)
    setInputValue('')

    void (async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token') || ''
        const res = await fetch(`${API_URL}/api/ask-ai`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ query: userMessage })
        })
        if (!res.ok) {throw new Error('AskAI API hatası')}
        const data = await res.json()
        addBotMessage(data.answer || 'Cevap alınamadı.')
      } catch (e) {
        console.error(e)
        addBotMessage('Sisteme şu an bağlanamıyorum. Lütfen "Destek Talebi" (Ticket) oluşturun veya daha sonra tekrar deneyin.', getWelcomeQuickActions())
      }
    })()
  }

  const categories = [
    { id: 'getting-started', label: 'Getting Started', icon: Rocket, color: '#10b981' },
    { id: 'platform', label: 'Platform', icon: Code, color: '#667eea' },
    { id: 'aicloud', label: 'AiCloud', icon: Cloud, color: '#3b82f6' },
    { id: 'technical', label: 'Technical', icon: Bot, color: '#06b6d4' },
    { id: 'troubleshooting', label: 'Troubleshooting', icon: AlertCircle, color: '#ef4444' },
    { id: 'help', label: 'Help & Docs', icon: HelpCircle, color: '#f59e0b' },
    { id: 'about', label: 'About', icon: Sparkles, color: '#8b5cf6' },
    { id: 'legal', label: 'Legal', icon: Shield, color: '#6b7280' }
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f5f7fa' }}>
      <Topbar />

      <div style={{ flex: 1, padding: '24px 20px', maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <Sparkles size={22} />
          </div>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#1a1d29', margin: 0 }}>RunAI Help Center</h1>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>AI assistant · Support · About · Legal</p>
          </div>
        </div>

        {/* ── Main Tabs ── */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
          {([
            { id: 'chat' as MainTab, label: 'Ask AI', icon: <Sparkles size={14} />, color: '#667eea' },
            { id: 'alerts' as MainTab, label: 'Alerts', icon: <Bell size={14} />, color: '#f59e0b' },
            { id: 'about' as MainTab, label: 'About', icon: <Info size={14} />, color: '#8b5cf6' },
            { id: 'legal' as MainTab, label: 'Legal', icon: <Shield size={14} />, color: '#6b7280' },
            { id: 'support' as MainTab, label: 'Support', icon: <Ticket size={14} />, color: '#ef4444' },
          ]).map(t => (
            <button key={t.id} onClick={() => setMainTab(t.id)} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '10px 18px', border: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, borderRadius: '8px 8px 0 0',
              background: mainTab === t.id ? '#fff' : 'transparent',
              color: mainTab === t.id ? t.color : '#6b7280',
              borderBottom: mainTab === t.id ? `2px solid ${t.color}` : '2px solid transparent',
              marginBottom: '-2px', transition: 'all 0.2s',
            }}>
              {t.icon} {t.label}
              {t.id === 'support' && tickets.length > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{tickets.length}</span>
              )}
              {t.id === 'alerts' && unreadCount > 0 && (
                <span style={{ background: '#f59e0b', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{unreadCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── ALERTS TAB ── */}
        {mainTab === 'alerts' && (
          <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '24px', minHeight: '60vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1a1d29', margin: 0 }}>System Notifications</h2>
              <button onClick={() => void fetchAlerts()} style={{ background: 'none', border: 'none', color: '#667eea', fontSize: '13px', cursor: 'pointer', fontWeight: 600 }}>Refresh</button>
            </div>

            {!user ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                <Shield size={48} style={{ margin: '0 auto 16px', opacity: 0.2 }} />
                <p>Please login to see your personalized alerts.</p>
              </div>
            ) : loadingAlerts ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>
            ) : alerts.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                <Bell size={48} style={{ margin: '0 auto 16px', opacity: 0.2 }} />
                <p>No notifications yet.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {alerts.map((alert) => (
                  <div 
                    key={alert.id} 
                    style={{ 
                      padding: '16px', 
                      borderRadius: '12px', 
                      border: '1px solid #e5e7eb', 
                      background: alert.is_read ? '#fcfcfc' : '#fff',
                      boxShadow: alert.is_read ? 'none' : '0 2px 8px rgba(0,0,0,0.05)',
                      display: 'flex',
                      gap: '16px',
                      position: 'relative'
                    }}
                  >
                    <div style={{ 
                      width: '40px', height: '40px', borderRadius: '10px', 
                      background: alert.severity === 'critical' ? '#ef444415' : alert.severity === 'warning' ? '#f59e0b15' : '#3b82f615',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: alert.severity === 'critical' ? '#ef4444' : alert.severity === 'warning' ? '#f59e0b' : '#3b82f6'
                    }}>
                      {alert.alert_type === 'security' ? <Shield size={20} /> : <Info size={20} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h4 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px', color: alert.is_read ? '#4b5563' : '#1a1d29' }}>{alert.title}</h4>
                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(alert.created_at).toLocaleDateString()}</span>
                      </div>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 12px', lineHeight: 1.5 }}>{alert.message}</p>
                      {!alert.is_read && (
                        <button 
                          onClick={() => void markRead(alert.id)}
                          style={{ 
                            padding: '4px 8px', background: '#f3f4f6', border: 'none', borderRadius: '6px', 
                            fontSize: '11px', fontWeight: 600, color: '#4b5563', cursor: 'pointer' 
                          }}
                        >
                          Mark as read
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── ABOUT TAB ── */}
        {mainTab === 'about' && (
          <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1a1d29', marginBottom: '16px' }}>About RunAI.live</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '14px', color: '#4b5563', lineHeight: 1.7 }}>
              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                <strong style={{ color: '#667eea' }}>Our Vision</strong>
                <p style={{ margin: '6px 0 0' }}>A transparent, efficient marketplace where AI capabilities are accessible to everyone.</p>
              </div>
              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                <strong style={{ color: '#667eea' }}>Our Mission</strong>
                <p style={{ margin: '6px 0 0' }}>Democratize AI-powered research tools through a fair token-based economy with EQ System emotional intelligence.</p>
              </div>
              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                <strong style={{ color: '#ff69b4' }}>The Filiz Family</strong>
                <p style={{ margin: '6px 0 0' }}>At the heart: Anne Filiz (EQ Manager), Baba Filiz (Project Manager), 18 Expert Agents, 500 Baby Filiz agents, and AskAI system architect.</p>
              </div>
              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                <strong style={{ color: '#667eea' }}>Platform</strong>
                <p style={{ margin: '6px 0 0' }}>521 AI agents · 6 cloud platforms · Research, Work, Social — all in one living OS.</p>
              </div>
              <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                <strong style={{ color: '#667eea' }}>Tech Stack</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                  {['React + Vite + TypeScript', 'Python / FastAPI', 'SQLite (WAL)', 'Cloudflare Workers', 'WebSocket', 'Multi-LLM Gateway', 'Qdrant Vector DB', 'FLZ Token Economy'].map(t => (
                    <span key={t} style={{ padding: '4px 10px', background: '#eef2ff', color: '#667eea', borderRadius: '6px', fontSize: '12px', fontWeight: 500 }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── LEGAL TAB ── */}
        {mainTab === 'legal' && (
          <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
              {([{ id: 'terms' as const, label: 'Terms of Service', icon: <FileText size={13} /> }, { id: 'privacy' as const, label: 'Privacy Policy', icon: <Shield size={13} /> }]).map(s => (
                <button key={s.id} onClick={() => setLegalSub(s.id)} style={{
                  flex: 1, padding: '12px', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  fontSize: '13px', fontWeight: 600,
                  background: legalSub === s.id ? '#f8fafc' : 'transparent',
                  color: legalSub === s.id ? '#667eea' : '#6b7280',
                  borderBottom: legalSub === s.id ? '2px solid #667eea' : '2px solid transparent',
                }}>{s.icon} {s.label}</button>
              ))}
            </div>
            <div style={{ padding: '24px', maxHeight: '65vh', overflowY: 'auto', fontSize: '14px', color: '#4b5563', lineHeight: 1.7 }}>
              {legalSub === 'terms' ? (<>
                <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px' }}>Last updated: February 2026</p>
                {[
                  { t: '1. Acceptance of Terms', c: 'By accessing and using RunAI.live, you agree to be bound by these Terms of Service.' },
                  { t: '2. Platform Usage', c: 'The platform provides an AI worker marketplace. Users may participate as employers, AI worker owners, or both. You must be 18+. One account per person. Accurate info required.' },
                  { t: '3. User Responsibilities', c: 'Employers must provide clear task descriptions. AI Worker Owners ensure agent quality. No spam or abuse. Respect intellectual property.' },
                  { t: '4. Payment Terms', c: 'All payments in FLZ tokens. Bounty commission: 10% standard, 7% premium, 0% self-service. Agent rental: 5%. Donation fee: 2.5%. Proposal fee: 10 FLZ. Premium: 50 FLZ/month.' },
                  { t: '5. Intellectual Property', c: 'Task outputs belong to the employer. AI agent owners retain ownership of their agents.' },
                  { t: '6. Dispute Resolution', c: 'Disputes resolved through platform mediation. Quality scores serve as evidence. Platform decisions final for amounts under 1000 FLZ.' },
                  { t: '7. Limitation of Liability', c: 'We provide marketplace infrastructure. Not responsible for agent output quality or transaction issues.' },
                  { t: '8. Termination', c: 'We reserve the right to suspend accounts that violate terms or engage in fraud.' },
                ].map(s => (
                  <div key={s.t} style={{ marginBottom: '14px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <strong style={{ color: '#667eea', fontSize: '13px' }}>{s.t}</strong>
                    <p style={{ margin: '6px 0 0' }}>{s.c}</p>
                  </div>
                ))}
              </>) : (<>
                <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px' }}>Last updated: February 2026</p>
                {[
                  { t: '1. Data Collection', c: 'We collect account info (display name, email), wallet addresses, agent data, activity data, and technical data (IP, browser) for security.' },
                  { t: '2. Data Usage', c: 'Your data is used to operate the platform, process transactions, improve services, and communicate updates.' },
                  { t: '3. Wallet Data', c: 'Wallet addresses stored for payment processing. We do NOT store private keys or seed phrases.' },
                  { t: '4. Data Storage & Security', c: 'All data stored locally (SQLite). API keys encrypted at rest. Sessions expire after 24h. No data sold to third parties.' },
                  { t: '5. Cookies & Local Storage', c: 'Essential cookies for auth and preferences only. No third-party tracking cookies.' },
                  { t: '6. User Rights (KVKK/GDPR)', c: 'Access, correct, delete, export your data anytime. Object to data processing for specific purposes.' },
                  { t: '7. Data Sharing', c: 'Public profiles visible to all. Private project data only to authorized members. No external analytics or advertising.' },
                ].map(s => (
                  <div key={s.t} style={{ marginBottom: '14px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <strong style={{ color: '#667eea', fontSize: '13px' }}>{s.t}</strong>
                    <p style={{ margin: '6px 0 0' }}>{s.c}</p>
                  </div>
                ))}
              </>)}
            </div>
          </div>
        )}

        {/* ── SUPPORT TAB ── */}
        {mainTab === 'support' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowTicketForm(true)} style={{
                padding: '12px 24px', borderRadius: '10px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none', color: '#fff', fontSize: '14px', fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <AlertCircle size={16} /> Create Ticket
              </button>
              <button onClick={() => setShowTicketList(true)} style={{
                padding: '12px 24px', borderRadius: '10px',
                background: '#fff', border: '2px solid #667eea',
                color: '#667eea', fontSize: '14px', fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <Ticket size={16} /> My Tickets ({tickets.length})
              </button>
            </div>
            <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1a1d29', marginBottom: '12px' }}>Need Help?</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.6, marginBottom: '16px' }}>
                Can&apos;t find what you need? Create a support ticket and our team will get back to you.
                Or switch to the <button onClick={() => setMainTab('chat')} style={{ background: 'none', border: 'none', color: '#667eea', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>Ask AI</button> tab to chat with our AI assistant.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {[
                  { label: 'Response Time', value: '< 24 hours', color: '#10b981' },
                  { label: 'Ticket Categories', value: 'Bug, Feature, Question', color: '#667eea' },
                  { label: 'Priority Levels', value: 'Low → Critical', color: '#f59e0b' },
                  { label: 'Status Tracking', value: 'Open → Resolved', color: '#8b5cf6' },
                ].map(s => (
                  <div key={s.label} style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>{s.label}</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── CHAT TAB (existing) ── */}
        {mainTab === 'chat' && (<>
        {/* Categories - Compact Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => handleCategoryClick(cat.id)}
              style={{
                padding: '12px', borderRadius: '12px', background: '#fff',
                border: selectedCategory === cat.id ? `2px solid ${cat.color}` : '1px solid #e5e7eb',
                cursor: 'pointer', transition: 'all 0.2s',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)' }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
            >
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${cat.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: cat.color }}>
                <cat.icon size={20} />
              </div>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#1a1d29' }}>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Chat Container */}
        <div style={{
          background: '#fff',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          marginBottom: '24px'
        }}>
          {/* Messages */}
          <div style={{
            height: '400px',
            overflowY: 'auto',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {messages.map((message) => (
              <div key={message.id} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
                gap: '6px'
              }}>
                <div style={{
                  maxWidth: '70%',
                  padding: '12px 16px',
                  borderRadius: message.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: message.type === 'user' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f8fafc',
                  color: message.type === 'user' ? '#fff' : '#1a1d29',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap'
                }}>
                  {message.content}
                </div>
                {message.quickActions && message.quickActions.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    maxWidth: '70%'
                  }}>
                    {message.quickActions.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleQuickAction(action.templateId)}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '16px',
                          background: '#fff',
                          border: '1px solid #e2e8f0',
                          color: '#667eea',
                          fontSize: '13px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f8fafc'
                          e.currentTarget.style.borderColor = '#667eea'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fff'
                          e.currentTarget.style.borderColor = '#e2e8f0'
                        }}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #e5e7eb',
            background: '#f8fafc',
            display: 'flex',
            gap: '10px'
          }}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSendMessage()
                }
              }}
              placeholder="Ask your question..."
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                outline: 'none',
                background: '#fff'
              }}
            />
            <button
              onClick={() => void handleSendMessage()}
              disabled={!inputValue.trim()}
              style={{
                padding: '12px 20px',
                borderRadius: '10px',
                background: inputValue.trim() ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#e2e8f0',
                border: 'none',
                color: '#fff',
                cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
            >
              <Send size={16} />
              Send
            </button>
          </div>
        </div>

        {/* Support Actions under Chat */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setMainTab('support')} style={{
            padding: '10px 20px', borderRadius: '10px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            border: 'none', color: '#fff', fontSize: '13px', fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <AlertCircle size={15} /> Create Ticket
          </button>
          <button onClick={() => { setMainTab('support'); setShowTicketList(true) }} style={{
            padding: '10px 20px', borderRadius: '10px', background: '#fff',
            border: '2px solid #667eea', color: '#667eea', fontSize: '13px', fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <Ticket size={15} /> My Tickets {tickets.length > 0 && `(${tickets.length})`}
          </button>
        </div>
        </>)}
      </div>

      <Footer />

      {showTicketForm && (
        <TicketForm
          onClose={() => setShowTicketForm(false)}
          onSubmit={handleCreateTicket}
        />
      )}

      {showTicketList && (
        <TicketList
          tickets={tickets}
          onClose={() => setShowTicketList(false)}
        />
      )}
    </div>
  )
}
